import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, QrCodeIcon, TrophyIcon, Share2Icon, SmartphoneIcon, MapPinIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { SectionHeading } from '../components/ui/SectionHeading';
import { ScrollReveal } from '../components/ui/ScrollReveal';
import { ConceptCard } from '../components/ui/ConceptCard';
export function HomePage() {
  return <div className="w-full" data-id="element-476">
      {/* HERO SECTION */}
      <section className="relative h-screen min-h-[800px] flex items-center justify-center overflow-hidden bg-brand-green-deep" data-id="element-477">
        {/* Cinematic Gradient Background Placeholder */}
        <div className="absolute inset-0 bg-hero-pattern opacity-80 mix-blend-multiply" data-id="element-478" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(184,150,90,0.15)_0%,transparent_60%)]" data-id="element-479" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center pt-24 pb-32" data-id="element-480">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: 0.2
        }} data-id="element-481">
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-heading text-brand-cream-light leading-tight mb-6 max-w-5xl mx-auto" data-id="element-482">
              Premium putting environments for luxury homes, resorts, and
              destinations.
            </h1>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: 0.4
        }} data-id="element-483">
            <p className="text-xl md:text-2xl font-body text-brand-cream/80 max-w-3xl mx-auto mb-12 leading-relaxed" data-id="element-484">
              PuttLab designs and builds unforgettable golf experiences that
              turn underused space into play, connection, and content.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: 0.6
        }} className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6" data-id="element-485">
            <Button variant="primary" size="lg" href="/concepts" data-id="element-486">
              Explore Concepts
            </Button>
            <Button variant="secondary" size="lg" href="/contact" className="!text-brand-cream-light !border-brand-cream-light/70 hover:!bg-brand-cream-light hover:!text-brand-green-deep" data-id="element-487">
              Book a Design Call
            </Button>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center z-0 pointer-events-none" animate={{
        y: [0, 10, 0]
      }} transition={{
        repeat: Infinity,
        duration: 2,
        ease: 'easeInOut'
      }} data-id="element-488">
          <span className="text-brand-gold text-xs tracking-widest uppercase mb-2 font-body" data-id="element-489">
            Scroll
          </span>
          <div className="w-[1px] h-12 bg-gradient-to-b from-brand-gold to-transparent" data-id="element-490" />
        </motion.div>
      </section>

      {/* VALUE PROPOSITION */}
      <section className="py-32 bg-brand-cream-light relative" data-id="element-491">
        <div className="max-w-7xl mx-auto px-6" data-id="element-492">
          <SectionHeading eyebrow="A New Category" title={<span data-id="element-494">
                A new category of{' '}
                <i className="text-brand-green" data-id="element-495">golf experience</i>
              </span>} subtitle="PuttLab is where design, play, and premium craftsmanship meet. We create luxury putting environments and social golf concepts for properties that want more than a standard amenity. From estate greens to resort activations, every PuttLab project is built to look exceptional, play beautifully, and bring people together." align="center" data-id="element-493" />
        </div>
      </section>

      {/* WHO WE SERVE */}
      <section className="py-24 bg-brand-cream" data-id="element-496">
        <div className="max-w-7xl mx-auto px-6" data-id="element-497">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-498">
            {[{
            title: 'Residential',
            desc: 'Luxury putting environments that elevate how you entertain, unwind, and enjoy your property.',
            link: '/residential'
          }, {
            title: 'Resorts & Hospitality',
            desc: 'Guest-facing golf experiences that turn unused space into a social, shareable amenity.',
            link: '/resorts'
          }, {
            title: 'Developers',
            desc: 'Distinctive golf-inspired amenities that add identity, play, and premium appeal to communities.',
            link: '/developers'
          }, {
            title: 'Activations & Events',
            desc: 'Branded golf experiences designed for gatherings, launches, weddings, and unforgettable moments.',
            link: '/activations'
          }].map((item, i) => <ScrollReveal key={item.title} delay={i * 0.1} direction="up" data-id="element-499">
                <a href={`#${item.link}`} className="group block h-full bg-brand-cream-light p-8 rounded-sm border border-brand-stone/20 hover:border-brand-gold/50 transition-all duration-300 hover:shadow-xl hover:-translate-y-1" data-id="element-500">
                  <div className="w-12 h-12 rounded-full bg-brand-green/10 flex items-center justify-center mb-6 group-hover:bg-brand-gold/20 transition-colors" data-id="element-501">
                    <MapPinIcon className="w-6 h-6 text-brand-green group-hover:text-brand-gold transition-colors" data-id="element-502" />
                  </div>
                  <h3 className="text-2xl font-heading text-brand-green-deep mb-4" data-id="element-503">
                    {item.title}
                  </h3>
                  <p className="text-brand-stone font-body leading-relaxed mb-6" data-id="element-504">
                    {item.desc}
                  </p>
                  <span className="inline-flex items-center text-brand-gold text-sm font-medium uppercase tracking-wider group-hover:translate-x-2 transition-transform" data-id="element-505">
                    Learn More <ArrowRightIcon className="ml-2 w-4 h-4" data-id="element-506" />
                  </span>
                </a>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* SIGNATURE CONCEPTS */}
      <section className="py-32 bg-brand-green-deep text-brand-cream-light" data-id="element-507">
        <div className="max-w-7xl mx-auto px-6" data-id="element-508">
          <SectionHeading eyebrow="Signature Concepts" title="Not just greens. Experiences." subtitle="From dramatic rooftop putting lounges to family-friendly challenge greens and wedding-weekend activations, PuttLab creates concepts that feel memorable before the first putt is even taken." light className="mb-20" data-id="element-509" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-510">
            <ConceptCard title="The Estate Green" gradientClass="bg-concept-estate" imageUrl="https://cdn.magicpatterns.com/uploads/3b62EKAAjmaPYQxrkuwQqy/estategreen.png" delay={0.1} data-id="element-511" />
            <ConceptCard title="The Social Green" gradientClass="bg-concept-social" imageUrl="https://cdn.magicpatterns.com/uploads/27Tjnbykmz8BtCjWmDRoGL/socialgreen.png" delay={0.2} data-id="element-512" />
            <ConceptCard title="The Sunset Green" gradientClass="bg-concept-sunset" imageUrl="https://cdn.magicpatterns.com/uploads/tvAkCsifm3oKpj9V73sDzg/sunet_green.png" delay={0.3} data-id="element-513" />
            <ConceptCard title="The Resort Green" gradientClass="bg-concept-resort" imageUrl="https://cdn.magicpatterns.com/uploads/s7i8aMmZ7R3Wg5fbHMCJgk/WhatsApp_Image_2026-03-17_at_12.28.34.jpg" delay={0.4} data-id="element-514" />
            <ConceptCard title="The Challenge Green" gradientClass="bg-concept-challenge" imageUrl="https://cdn.magicpatterns.com/uploads/97PxqU37s7pQE7TC6nRynb/challenge_green.png" delay={0.5} data-id="element-515" />
            <ConceptCard title="The Wedding Green" gradientClass="bg-concept-wedding" imageUrl="https://cdn.magicpatterns.com/uploads/o2kbb1M8SM3uegyLFntqUo/wedding_golf.png" delay={0.6} data-id="element-516" />
          </div>
        </div>
      </section>

      {/* WHY PUTTLAB */}
      <section className="py-32 bg-brand-cream-light" data-id="element-517">
        <div className="max-w-7xl mx-auto px-6" data-id="element-518">
          <SectionHeading eyebrow="The Difference" title="Why PuttLab" align="center" className="mb-20" data-id="element-519" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-16" data-id="element-520">
            {[{
            title: 'Design-led',
            desc: 'Every project begins with experience, flow, and visual impact.'
          }, {
            title: 'Social by nature',
            desc: 'Our environments are built for connection, challenge, and replayability.'
          }, {
            title: 'Built for sharing',
            desc: 'Each space is designed to create photo moments, bragging rights, and memorable wins.'
          }, {
            title: 'Hospitality ready',
            desc: 'Perfect for resorts, clubs, and venues that want a more engaging amenity.'
          }, {
            title: 'Crafted to perform',
            desc: 'Behind the beauty is real contouring, execution, and build knowledge.'
          }, {
            title: 'Backed by TurfsUp!',
            desc: 'Built on nearly a decade of experience and 1000+ completed projects in North America.'
          }].map((pillar, i) => <ScrollReveal key={pillar.title} delay={i * 0.1} data-id="element-521">
                <div className="border-t border-brand-stone/30 pt-6" data-id="element-522">
                  <h3 className="text-2xl font-heading text-brand-green-deep mb-3" data-id="element-523">
                    {pillar.title}
                  </h3>
                  <p className="text-brand-stone font-body" data-id="element-524">{pillar.desc}</p>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* PUTTLAB PLAY */}
      <section className="py-32 bg-brand-charcoal text-brand-cream-light relative overflow-hidden" data-id="element-525">
        {/* Tech pattern background */}
        <div className="absolute inset-0 opacity-5 bg-[radial-gradient(#C4A97D_1px,transparent_1px)] [background-size:32px_32px]" data-id="element-526" />

        <div className="max-w-7xl mx-auto px-6 relative z-10" data-id="element-527">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-528">
            <div data-id="element-529">
              <ScrollReveal data-id="element-530">
                <span className="block text-brand-gold text-xs font-bold tracking-[0.2em] uppercase mb-4" data-id="element-531">
                  Digital Layer
                </span>
                <h2 className="text-4xl md:text-5xl font-heading leading-tight mb-6" data-id="element-532">
                  A putting experience that lives beyond the surface.
                </h2>
                <p className="text-lg font-body text-brand-cream/80 mb-10 leading-relaxed" data-id="element-533">
                  With PuttLab Play, guests scan a QR code to unlock game modes,
                  keep score, join challenges, view leaderboards, and generate
                  shareable winner cards. It's a modern digital layer that turns
                  a putting green into an experience people want to replay and
                  post.
                </p>
              </ScrollReveal>

              <div className="space-y-6" data-id="element-534">
                {[{
                icon: QrCodeIcon,
                text: 'Scan to start'
              }, {
                icon: SmartphoneIcon,
                text: 'Choose your challenge'
              }, {
                icon: TrophyIcon,
                text: 'Keep score'
              }, {
                icon: Share2Icon,
                text: 'Share the win'
              }].map((feature, i) => <ScrollReveal key={feature.text} delay={0.2 + i * 0.1} data-id="element-535">
                    <div className="flex items-center space-x-4" data-id="element-536">
                      <div className="w-10 h-10 rounded-full bg-brand-green flex items-center justify-center" data-id="element-537">
                        <feature.icon className="w-5 h-5 text-brand-gold" data-id="element-538" />
                      </div>
                      <span className="font-body text-lg text-brand-cream-light" data-id="element-539">
                        {feature.text}
                      </span>
                    </div>
                  </ScrollReveal>)}
              </div>

              <ScrollReveal delay={0.6} data-id="element-540">
                <div className="mt-12" data-id="element-541">
                  <Button variant="primary" href="/play" data-id="element-542">
                    Explore PuttLab Play
                  </Button>
                </div>
              </ScrollReveal>
            </div>

            {/* Mockup visual — Winner/Share screen */}
            <ScrollReveal direction="left" delay={0.3} data-id="element-543">
              <div className="relative mx-auto w-full max-w-sm aspect-[9/19] bg-brand-green-deep rounded-[3rem] border-8 border-brand-dark-gray shadow-2xl overflow-hidden flex flex-col" data-id="element-544">
                <div className="absolute top-0 inset-x-0 h-6 bg-brand-dark-gray rounded-b-3xl w-1/2 mx-auto z-20" data-id="element-545" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(184,150,90,0.2)_0%,transparent_60%)]" data-id="element-546" />
                <div className="flex-1 bg-gradient-to-b from-brand-green to-brand-green-deep p-6 pt-14 flex flex-col relative z-10" data-id="element-547">
                  {/* App header */}
                  <div className="text-center mb-6" data-id="element-548">
                    <span className="text-brand-gold text-[9px] font-bold uppercase tracking-[0.25em] block mb-1" data-id="element-549">
                      🏆 Course Champion
                    </span>
                    <h4 className="font-heading text-3xl text-brand-cream-light mb-0.5" data-id="element-550">
                      Alex M.
                    </h4>
                    <p className="text-xs font-body text-brand-cream/50 uppercase tracking-widest" data-id="element-551">
                      The Sunset Green
                    </p>
                  </div>

                  {/* Score card */}
                  <div className="bg-brand-charcoal/40 backdrop-blur-md rounded-2xl p-5 border border-brand-cream/10 mb-4 text-center" data-id="element-552">
                    <span className="text-brand-cream/50 text-[10px] uppercase tracking-widest block mb-1" data-id="element-553">
                      Final Score
                    </span>
                    <span className="text-6xl font-heading text-brand-gold leading-none" data-id="element-554">
                      18
                    </span>
                    <span className="text-brand-cream/40 text-xs block mt-1 uppercase tracking-wider" data-id="element-555">
                      Strokes · 9 Holes
                    </span>
                  </div>

                  {/* Leaderboard preview */}
                  <div className="bg-brand-cream-light/5 rounded-xl border border-brand-cream/10 mb-4 overflow-hidden flex-1" data-id="element-556">
                    <div className="px-4 py-2 border-b border-brand-cream/10" data-id="element-557">
                      <span className="text-[9px] text-brand-gold font-bold uppercase tracking-widest" data-id="element-558">
                        Leaderboard
                      </span>
                    </div>
                    <div className="px-4 py-2.5 flex items-center justify-between border-b border-brand-cream/5" data-id="element-559">
                      <div className="flex items-center space-x-2" data-id="element-560">
                        <span className="text-brand-gold text-xs font-bold w-4" data-id="element-561">
                          1
                        </span>
                        <span className="text-brand-cream-light text-sm font-medium" data-id="element-562">
                          Alex M.
                        </span>
                      </div>
                      <span className="text-brand-gold text-sm font-heading" data-id="element-563">
                        18
                      </span>
                    </div>
                    <div className="px-4 py-2.5 flex items-center justify-between border-b border-brand-cream/5" data-id="element-564">
                      <div className="flex items-center space-x-2" data-id="element-565">
                        <span className="text-brand-cream/40 text-xs font-bold w-4" data-id="element-566">
                          2
                        </span>
                        <span className="text-brand-cream/70 text-sm" data-id="element-567">
                          Sarah K.
                        </span>
                      </div>
                      <span className="text-brand-cream/70 text-sm font-heading" data-id="element-568">
                        21
                      </span>
                    </div>
                    <div className="px-4 py-2.5 flex items-center justify-between" data-id="element-569">
                      <div className="flex items-center space-x-2" data-id="element-570">
                        <span className="text-brand-cream/40 text-xs font-bold w-4" data-id="element-571">
                          3
                        </span>
                        <span className="text-brand-cream/70 text-sm" data-id="element-572">
                          Mike R.
                        </span>
                      </div>
                      <span className="text-brand-cream/70 text-sm font-heading" data-id="element-573">
                        24
                      </span>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="grid grid-cols-2 gap-3" data-id="element-574">
                    <div className="bg-brand-gold text-brand-charcoal rounded-xl py-3 text-center font-bold text-xs uppercase tracking-wider" data-id="element-575">
                      Share Result
                    </div>
                    <div className="bg-brand-cream-light/10 text-brand-cream-light rounded-xl py-3 text-center font-bold text-xs uppercase tracking-wider border border-brand-cream/10" data-id="element-576">
                      Play Again
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* TRANSFORMATIONS */}
      <section className="py-32 bg-brand-cream" data-id="element-577">
        <div className="max-w-7xl mx-auto px-6" data-id="element-578">
          <SectionHeading eyebrow="Transformations" title="From underused space to unforgettable destination." subtitle="PuttLab sees potential where others see empty space." className="mb-16" data-id="element-579" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-580">
            {[{
            before: 'A side lawn',
            after: 'A Sunset Green'
          }, {
            before: 'A rooftop',
            after: 'A social golf lounge'
          }, {
            before: 'A resort pathway',
            after: 'A guest challenge trail'
          }, {
            before: 'A private backyard',
            after: 'The best place to gather'
          }].map((item, i) => <ScrollReveal key={i} delay={i * 0.1} data-id="element-581">
                <div className="bg-brand-cream-light p-8 md:p-12 rounded-sm border border-brand-stone/20 flex flex-col justify-center min-h-[200px]" data-id="element-582">
                  <p className="text-brand-stone font-body text-lg mb-2 line-through decoration-brand-stone/50" data-id="element-583">
                    {item.before}
                  </p>
                  <p className="text-3xl font-heading text-brand-green-deep" data-id="element-584">
                    becomes <i className="text-brand-gold" data-id="element-585">{item.after}</i>.
                  </p>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-32 bg-brand-cream-light" data-id="element-586">
        <div className="max-w-7xl mx-auto px-6" data-id="element-587">
          <SectionHeading eyebrow="How We Work" title="Process" align="center" className="mb-20" data-id="element-588" />

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative" data-id="element-589">
            {/* Connecting line for desktop */}
            <div className="hidden md:block absolute top-12 left-0 w-full h-[1px] bg-brand-stone/30" data-id="element-590" />

            {[{
            num: '1',
            title: 'Discover',
            desc: 'We learn the property, the audience, and the opportunity.'
          }, {
            num: '2',
            title: 'Design',
            desc: 'We shape a concept that blends play, beauty, and real-world usability.'
          }, {
            num: '3',
            title: 'Build',
            desc: 'We execute with premium materials, contour precision, and craftsmanship.'
          }, {
            num: '4',
            title: 'Activate',
            desc: 'We help turn the finished space into an experience people actually engage with.'
          }].map((step, i) => <ScrollReveal key={step.num} delay={i * 0.2} className="relative z-10" data-id="element-591">
                <div className="flex flex-col items-center text-center" data-id="element-592">
                  <div className="w-24 h-24 rounded-full bg-brand-cream border-4 border-brand-cream-light flex items-center justify-center mb-6 shadow-lg" data-id="element-593">
                    <span className="text-3xl font-heading text-brand-gold" data-id="element-594">
                      {step.num}
                    </span>
                  </div>
                  <h3 className="text-2xl font-heading text-brand-green-deep mb-3" data-id="element-595">
                    {step.title}
                  </h3>
                  <p className="text-brand-stone font-body" data-id="element-596">{step.desc}</p>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* CREDIBILITY */}
      <section className="py-24 bg-brand-green text-brand-cream-light" data-id="element-597">
        <div className="max-w-4xl mx-auto px-6 text-center" data-id="element-598">
          <ScrollReveal data-id="element-599">
            <h2 className="text-3xl md:text-4xl font-heading mb-8 leading-relaxed" data-id="element-600">
              PuttLab is the next evolution of TurfsUp!'s real-world experience.
            </h2>
            <p className="text-lg font-body text-brand-cream/80 mb-12" data-id="element-601">
              With nearly a decade of work and more than 1000+ completed
              projects in North America, we're bringing proven build capability
              into a more premium, global, and experience-led future.
            </p>
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-8 sm:space-y-0 sm:space-x-16 border-t border-brand-cream/20 pt-12" data-id="element-602">
              <div data-id="element-603">
                <span className="block text-5xl font-heading text-brand-gold mb-2" data-id="element-604">
                  10<span className="text-3xl" data-id="element-605">yrs</span>
                </span>
                <span className="text-sm font-body uppercase tracking-widest text-brand-cream/60" data-id="element-606">
                  Experience
                </span>
              </div>
              <div data-id="element-607">
                <span className="block text-5xl font-heading text-brand-gold mb-2" data-id="element-608">
                  1000<span className="text-3xl" data-id="element-609">+</span>
                </span>
                <span className="text-sm font-body uppercase tracking-widest text-brand-cream/60" data-id="element-610">
                  Projects Completed
                </span>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-32 bg-brand-green-deep text-brand-cream-light relative overflow-hidden" data-id="element-611">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(184,150,90,0.2)_0%,transparent_50%)]" data-id="element-612" />
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10" data-id="element-613">
          <ScrollReveal data-id="element-614">
            <h2 className="text-5xl md:text-7xl font-heading mb-8" data-id="element-615">
              Let's create something unforgettable.
            </h2>
            <p className="text-xl font-body text-brand-cream/80 mb-12 max-w-2xl mx-auto" data-id="element-616">
              Whether you're designing a luxury residence, activating a resort,
              or building a standout amenity, PuttLab can help you turn the idea
              into something people actually remember.
            </p>
            <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6" data-id="element-617">
              <Button variant="primary" size="lg" href="/contact" data-id="element-618">
                Start Your Project
              </Button>
              <Button variant="secondary" size="lg" href="/contact" className="!text-brand-cream-light !border-brand-cream-light/70 hover:!bg-brand-cream-light hover:!text-brand-green-deep" data-id="element-619">
                Talk to PuttLab
              </Button>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}